import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Label } from './ui/label';
import { 
  Building2, Mail, Phone, MapPin, Calendar, ExternalLink,
  Package, Ticket, FileText, Users, Activity, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatInCST, formatDateOnly } from '../utils/timezone';

export default function CustomerDetailDialog({ customer, open, onOpenChange, onUpdate }) {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState({
    assets: false,
    tickets: false,
    invoices: false
  });

  useEffect(() => {
    if (open && customer) {
      loadCustomerData();
    }
  }, [open, customer]);

  const loadCustomerData = async () => {
    if (!customer) return;
    
    // Load all customer-related data
    setLoading({ assets: true, tickets: true, invoices: true });
    
    try {
      const [assetsRes, ticketsRes, invoicesRes] = await Promise.all([
        api.get(`/assets?customer_id=${customer.id}&limit=100`).catch(() => ({ data: [] })),
        api.get(`/tickets?customer_id=${customer.id}&limit=100`).catch(() => ({ data: [] })),
        api.get(`/invoices?customer_id=${customer.id}&limit=100`).catch(() => ({ data: [] }))
      ]);
      
      setAssets(assetsRes.data);
      const ticketsData = ticketsRes.data;
      setTickets(Array.isArray(ticketsData) ? ticketsData : ticketsData.tickets || []);
      const invoicesData = invoicesRes.data;
      setInvoices(Array.isArray(invoicesData) ? invoicesData : invoicesData.invoices || []);
    } catch (error) {
      console.error('Error loading customer data:', error);
    } finally {
      setLoading({ assets: false, tickets: false, invoices: false });
    }
  };

  const handleViewFullPage = () => {
    navigate(`/customers/${customer.id}`);
    onOpenChange(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-blue-100 text-blue-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  if (!customer) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="customer-detail-dialog">
        <DialogHeader className="border-b pb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <DialogTitle className="text-2xl font-bold flex items-center gap-3">
                <Building2 className="h-6 w-6 text-blue-600" />
                {customer.company_name}
              </DialogTitle>
              <div className="flex items-center gap-3 mt-2">
                <Badge className={getStatusColor(customer.status)}>
                  {customer.status}
                </Badge>
                {customer.contact_name && (
                  <span className="text-sm text-gray-600">Contact: {customer.contact_name}</span>
                )}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleViewFullPage}
              data-testid="view-full-page-button"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Full Page View
            </Button>
          </div>
        </DialogHeader>

        {/* Customer Overview Info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4 border-b">
          {customer.email && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Email</p>
              <p className="text-sm font-medium flex items-center gap-2">
                <Mail className="h-3 w-3 text-gray-400" />
                {customer.email}
              </p>
            </div>
          )}
          {customer.phone && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Phone</p>
              <p className="text-sm font-medium flex items-center gap-2">
                <Phone className="h-3 w-3 text-gray-400" />
                {customer.phone}
              </p>
            </div>
          )}
          {(customer.city || customer.state) && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Location</p>
              <p className="text-sm font-medium flex items-center gap-2">
                <MapPin className="h-3 w-3 text-gray-400" />
                {customer.city}, {customer.state}
              </p>
            </div>
          )}
          <div>
            <p className="text-xs text-gray-500 mb-1">Customer Since</p>
            <p className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-3 w-3 text-gray-400" />
              {formatInCST(customer.created_at, 'MMM d, yyyy')}
            </p>
          </div>
        </div>

        {/* Tabs for related data */}
        <Tabs defaultValue="overview" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="assets" data-testid="tab-assets">
              Assets ({assets.length})
            </TabsTrigger>
            <TabsTrigger value="tickets" data-testid="tab-tickets">
              Tickets ({tickets.length})
            </TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">
              Invoices ({invoices.length})
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto mt-4">
            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4 mt-0">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Total Assets
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{assets.length}</div>
                    <p className="text-xs text-gray-500 mt-1">
                      {assets.filter(a => a.status === 'active').length} active
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                      <Ticket className="h-4 w-4" />
                      Open Tickets
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">
                      {tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {tickets.length} total tickets
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Invoices
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{invoices.length}</div>
                    <p className="text-xs text-gray-500 mt-1">
                      {invoices.filter(i => i.status === 'paid').length} paid
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Customer Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Customer Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Company Name</Label>
                      <p className="text-sm font-medium mt-1">{customer.company_name}</p>
                    </div>
                    {customer.contact_name && (
                      <div>
                        <Label className="text-xs text-gray-500">Contact Person</Label>
                        <p className="text-sm font-medium mt-1">{customer.contact_name}</p>
                      </div>
                    )}
                    {customer.email && (
                      <div>
                        <Label className="text-xs text-gray-500">Email</Label>
                        <p className="text-sm font-medium mt-1">{customer.email}</p>
                      </div>
                    )}
                    {customer.phone && (
                      <div>
                        <Label className="text-xs text-gray-500">Phone</Label>
                        <p className="text-sm font-medium mt-1">{customer.phone}</p>
                      </div>
                    )}
                  </div>
                  {customer.address && (
                    <div>
                      <Label className="text-xs text-gray-500">Address</Label>
                      <p className="text-sm font-medium mt-1">
                        {customer.address}
                        {customer.city && customer.state && (
                          <><br />{customer.city}, {customer.state} {customer.zip_code}</>
                        )}
                      </p>
                    </div>
                  )}
                  {customer.notes && (
                    <div>
                      <Label className="text-xs text-gray-500">Notes</Label>
                      <p className="text-sm text-gray-600 mt-1">{customer.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Assets Tab */}
            <TabsContent value="assets" className="mt-0">
              {loading.assets ? (
                <div className="text-center py-8 text-gray-500">Loading assets...</div>
              ) : assets.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  No assets for this customer
                </div>
              ) : (
                <div className="space-y-2">
                  {assets.map((asset) => (
                    <Card key={asset.id} className="hover:bg-gray-50 transition-colors">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm">{asset.name}</h4>
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-600">
                              <span>{asset.asset_type}</span>
                              {asset.serial_number && (
                                <><span>•</span><span className="font-mono">{asset.serial_number}</span></>
                              )}
                              {asset.manufacturer && (
                                <><span>•</span><span>{asset.manufacturer} {asset.model}</span></>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={getStatusColor(asset.status)}>{asset.status}</Badge>
                            {asset.ninjarmm_device_id && (
                              <Badge className="bg-blue-100 text-blue-800 text-xs">NinjaRMM</Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Tickets Tab */}
            <TabsContent value="tickets" className="mt-0">
              {loading.tickets ? (
                <div className="text-center py-8 text-gray-500">Loading tickets...</div>
              ) : tickets.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Ticket className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  No tickets for this customer
                </div>
              ) : (
                <div className="space-y-2">
                  {tickets.map((ticket) => (
                    <Card 
                      key={ticket.id} 
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => { onOpenChange(false); navigate(`/tickets/${ticket.id}`); }}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-mono text-gray-500">{ticket.ticket_number}</span>
                              <Badge className={getPriorityColor(ticket.priority)}>
                                {ticket.priority}
                              </Badge>
                              <Badge className={getStatusColor(ticket.status)}>
                                {ticket.status}
                              </Badge>
                            </div>
                            <h4 className="font-semibold text-sm">{ticket.title}</h4>
                            {ticket.description && (
                              <p className="text-xs text-gray-600 mt-1 line-clamp-1">
                                {ticket.description}
                              </p>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatInCST(ticket.created_at, 'MMM d, h:mm a')}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Invoices Tab */}
            <TabsContent value="invoices" className="mt-0">
              {loading.invoices ? (
                <div className="text-center py-8 text-gray-500">Loading invoices...</div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  No invoices for this customer
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id} className="cursor-pointer hover:bg-gray-50" onClick={() => { onOpenChange(false); navigate(`/invoices/${invoice.id}`); }}>
                        <TableCell className="font-mono text-sm">{invoice.invoice_number}</TableCell>
                        <TableCell className="text-sm">
                          {invoice.issue_date ? formatDateOnly(invoice.issue_date, 'MMM d, yyyy') : '-'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {invoice.due_date ? formatDateOnly(invoice.due_date, 'MMM d, yyyy') : '-'}
                        </TableCell>
                        <TableCell className="text-sm font-semibold">
                          ${invoice.total_amount?.toFixed(2) || '0.00'}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(invoice.status)}>
                            {invoice.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="activity" className="mt-0">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Activity className="h-4 w-4" />
                  <span>Recent Activity</span>
                </div>
                
                {[...tickets, ...assets, ...invoices]
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .slice(0, 10)
                  .map((item, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {item.title || item.name || item.invoice_number}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {item.ticket_number ? 'Ticket' : item.asset_type ? 'Asset' : 'Invoice'} • 
                          {formatInCST(item.created_at, 'MMM d, h:mm a')}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
